"use client"
import { useMediaQuery } from "@react-hooks-library/core";
import { gql } from "graphql-request";
import { useMemo, useRef } from "react";
import { getOrgSize } from "src/components/images/imageFunctions";
import { urlForAsset } from "src/lib/client/imageUrls";
import { useFilminfo } from "src/lib/client/useFilminfo";
import type { Maybe, MovieClip, MovieClipVersion } from "src/lib/movieinfo/movieinfotypes";
import type IPremiumLandingPage from "src/lib/types/IPremiumLandingPage";
import styles from "./Hero.module.css";

//#region [Props]
type HeroProps = {
    landingpage: IPremiumLandingPage;
};
//#endregion

//#region [Component]
export default function Hero({ landingpage }: HeroProps) {
    const isMobile = useMediaQuery("(max-width: 549px)");
    const videoRef = useRef(null);


    const noVideo = (isMobile && !landingpage.bgVideoIdMobile) || (!isMobile && !landingpage.bgVideoIdDesktop);

    const { fiLoading, fiData } = useFilminfo(BGVIDEO_URLS_QUERY, { clipIdVertical: landingpage.bgVideoIdMobile, clipIdHorizontal: landingpage.bgVideoIdDesktop }, { active: !noVideo });

    const [videoSrc, posterSrc] = useMemo(() => {
        let videoSrc: string | undefined | null;
        let posterSrc: string | undefined;
        if (!fiLoading && fiData) {
            //console.debug("fiData", fiData);
            const clips = fiData.movieQuery as Clips | null;
            const video = isMobile ? clips?.verticalClip?.movieClipVersions : clips?.horizontalClip?.movieClipVersions;
            if (video) {
                const findr = (size: string) => { return (v: Maybe<MovieClipVersion>) => v?.name === size };
                videoSrc = video.find(findr("medium"))?.url;
                const width = window?.innerWidth;
                if (width >= 1920) {
                    videoSrc = video.find(findr("1080p"))?.url ?? video.find(findr("hd"))?.url ?? videoSrc;
                } else if (width >= 1000) {
                    videoSrc = video.find(findr("hd"))?.url || videoSrc;
                }

                const posterJsonVert = landingpage.vertStill ? [
                    { mq: "(max-width: 640px) and (orientation: portrait)", img: 640, orientation: "portrait" },
                    { mq: "(min-width: 641px and max-width: 1000px) and (orientation: portrait)", img: 1000, orientation: "portrait" },
                    { mq: "(min-width: 1000px and max-width: 1500px) and (orientation: portrait)", img: 1500, orientation: "portrait" },
                    { mq: "(min-width: 1501px) and (orientation: portrait)", img: 1920, orientation: "portrait" }
                ] : [];

                const posterJsonHoriz = landingpage.horizStill ? [
                    { mq: "(max-width: 640px) and (orientation: landscape)", img: 640, orientation: "landscape" },
                    { mq: "(min-width: 641px and max-width: 1000px) and (orientation: landscape)", img: 1000, orientation: "landscape" },
                    { mq: "(min-width: 1000px and max-width: 1500px) and (orientation: landscape)", img: 1500, orientation: "landscape" },
                    { mq: "(min-width: 1501px) and (orientation: landscape)", img: 1920, orientation: "landscape" }
                ] : [];

                const posterJson = (posterJsonVert
                    .concat(posterJsonHoriz)
                    .find(p => window.matchMedia(p.mq).matches));

                if (posterJson) {
                    const img = posterJson.orientation === "portrait" ? landingpage.vertStill : landingpage.horizStill;
                    const assetRef = img?.asset?._ref;
                    if (assetRef && assetRef.startsWith("image-")) {
                        const size = getOrgSize(assetRef);
                        const w = size.width;
                        const h = size.height;
                        posterSrc = urlForAsset(assetRef)
                            .auto("format")
                            .width(w).height(h)
                            .url();
                    }
                }

                return [videoSrc, posterSrc];
            }
        }
        return [null, null];
    }, [fiLoading, fiData, isMobile, landingpage.horizStill, landingpage.vertStill]);

    return (
        <div className={styles.hero}>
            <div className={styles.videoWrapper}>
                {!noVideo && !fiLoading && videoSrc && (
                    <video
                        ref={videoRef}
                        className={styles.video}
                        src={videoSrc}
                        poster={posterSrc ?? undefined}
                        autoPlay
                        muted
                        loop
                        playsInline
                    />
                )}
                <div
                    className={styles.bottomShade}
                    style={{
                        "--bg-color-start": `${landingpage.bgColor.hex}00`,
                        "--bg-color-end": `${landingpage.bgColor.hex}ff`
                    } as React.CSSProperties}
                />
            </div>
        </div>
    );
}
//#endregion

const BGVIDEO_URLS_QUERY = gql`
query ($clipIdVertical: Int!, $clipIdHorizontal: Int!) {
	movieQuery {
		verticalClip: getMovieClip(clipId: $clipIdVertical) {
			movieClipVersions {
				url
				name
			}
		}
    	horizontalClip: getMovieClip(clipId: $clipIdHorizontal) {
			movieClipVersions {
				url
				name
			}
    	}
  	}
}
`;

type Clips = {
    verticalClip: MovieClip;
    horizontalClip: MovieClip;
};
